import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import type { FishingSpotListItem } from '@models/fishing-spots';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from '@services/confirmation.service';
import { CrmFishingSpotsService } from '@services/crm-fishing-spots.service';
import { ToastService } from '@services/toast.service';
import { BoolComponent } from '@shared/components/bool/bool.component';
import { catchError, firstValueFrom, of } from 'rxjs';

@Component({
  selector: 'app-fishing-spot-list',
  templateUrl: './fishing-spot-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    TranslatePipe,
    BoolComponent,
  ],
})
export class FishingSpotListComponent {
  readonly waterId = input.required<number>();

  private readonly fishingSpotsService = inject(CrmFishingSpotsService);
  private readonly router = inject(Router);
  private readonly confirmation = inject(ConfirmationService);
  private readonly toastService = inject(ToastService);
  private readonly translate = inject(TranslateService);

  readonly deletingId = signal<number | null>(null);
  readonly displayedColumns = ['number', 'spots', 'isActive', 'isDummy', 'actions'];

  readonly spotsResource = rxResource({
    params: () => ({ waterId: this.waterId() }),
    stream: ({ params }) =>
      this.fishingSpotsService.getSpots(params.waterId).pipe(
        catchError(() => {
          this.toastService.showError(this.translate.instant('waters.fishing-spots.load-failed'));
          return of([] as FishingSpotListItem[]);
        }),
      ),
  });

  readonly spots = computed(() => this.spotsResource.value() ?? []);
  readonly isLoading = this.spotsResource.isLoading;

  editSpot(id: number): void {
    this.router.navigate(['/waters/edit', this.waterId(), 'spots', 'edit', id]);
  }

  async deleteSpot(id: number): Promise<void> {
    const confirmed = await this.confirmation.open({
      title: this.translate.instant('waters.fishing-spots.delete-title'),
      prompt: this.translate.instant('waters.fishing-spots.delete-confirm'),
    });
    if (!confirmed) return;

    this.deletingId.set(id);
    try {
      await firstValueFrom(this.fishingSpotsService.deleteSpot(this.waterId(), id));
      this.toastService.showSuccess(this.translate.instant('waters.fishing-spots.delete-success'));
      this.spotsResource.reload();
    } catch {
      this.toastService.showError(this.translate.instant('waters.fishing-spots.delete-failed'));
    } finally {
      this.deletingId.set(null);
    }
  }
}
